// Split out of src/components/ChildView.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). ChildView.jsx
// re-exports everything it exported before, so existing imports keep working.

import { GoalCard, GoalFormModal } from "../ChildHero.jsx";
import { ActivityList, ActivityRow, CoinIcon, Icon, uploadResizedImage } from "../Common.jsx";
import { AskToBuyCard, AskToBuySheet } from "../AskToBuy.jsx";
import { openAsk } from "../../lib/askToBuy.js";
import { uid } from "../../lib/constants.js";
import { t as ct } from "../../lib/content.js";
import { useBackLayer } from "../../lib/backstack.js";
import { activityRowStyle, fmtActivityStamp, goalPricePence, goalTargetTokens, tokensToPence } from "../../lib/utils.js";
import { CollapsibleSection } from "./kidShared.jsx";
import { SavingsVaultCard } from "./Vault.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* --------------------------- Rewards tab (kid) --------------------------- */


// LB-385 (Alan, 30 Sep): the goal form, lifted out of RewardsTab so kid Home's wish list opens
// the SAME form ("Save up for it") without a second copy of it. Moved verbatim: the state, the
// photo upload and the save/remove rules are RewardsTab's own, unchanged. Returns the three
// entry points and the modal to render.
function useGoalEditor({ code, goals, rate, onUpdateGoals, kidTheme }) {
  const [goalFormOpen, setGoalFormOpen] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [goalTitle, setGoalTitle] = useState("");
  const [goalTarget, setGoalTarget] = useState("");
  const [goalPence, setGoalPence] = useState(null); // LB-355: the price as entered
  const [goalImage, setGoalImage] = useState(null);
  const [goalImageError, setGoalImageError] = useState("");
  const [goalImageUploading, setGoalImageUploading] = useState(false);
  function startAddGoal() {
    setEditingGoalId(null); setGoalTitle(""); setGoalTarget(""); setGoalPence(null); setGoalImage(null); setGoalImageError(""); setGoalFormOpen(true);
  }
  function startEditGoal(g) {
    setEditingGoalId(g.id); setGoalTitle(g.title); setGoalTarget(String(goalTargetTokens(g, rate))); setGoalPence(goalPricePence(g, rate)); setGoalImage(g.image || null); setGoalImageError(""); setGoalFormOpen(true);
  }
  async function handleGoalPhoto(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setGoalImageError(""); setGoalImageUploading(true);
    try {
      const url = await uploadResizedImage(code, file, 400, 0.85);
      setGoalImage(url);
    } catch (err) {
      setGoalImageError(err.message || "Couldn't use that photo.");
    } finally {
      setGoalImageUploading(false);
    }
  }
  function saveGoal() {
    const t = parseInt(goalTarget, 10);
    if (!goalTitle.trim() || !t || t <= 0) return;
    // LB-355: stored as money however it was entered.
    const targetPence = goalPence > 0 ? goalPence : tokensToPence(t, rate);
    if (editingGoalId) {
      onUpdateGoals(goals.map((g) => { if (g.id !== editingGoalId) return g; const { targetTokens, ...rest } = g; void targetTokens; return { ...rest, title: goalTitle.trim(), targetPence, image: goalImage || null }; }));
    } else {
      onUpdateGoals([...goals, { id: uid(), title: goalTitle.trim(), targetPence, image: goalImage || null }]);
    }
    setGoalFormOpen(false); setEditingGoalId(null); setGoalTitle(""); setGoalTarget(""); setGoalImage(null); setGoalImageError("");
  }
  function removeGoal(id) {
    onUpdateGoals(goals.filter((g) => g.id !== id));
  }
  // Only one goal can be the starred "top goal" shown on Home — starring a new one un-stars
  // the rest rather than tracking a separate topGoalId field.
  function toggleStar(id) {
    onUpdateGoals(goals.map((g) => ({ ...g, starred: g.id === id ? !g.starred : false })));
  }
  const modal = goalFormOpen ? (
    <GoalFormModal
      editingGoalId={editingGoalId}
      title={goalTitle} onTitleChange={setGoalTitle}
      target={goalTarget} targetPence={goalPence} onTargetChange={(t, p) => { setGoalTarget(t); setGoalPence(p ?? null); }}
      rate={rate}
      image={goalImage} imageError={goalImageError} imageUploading={goalImageUploading}
      onPhotoChange={handleGoalPhoto}
      onSave={saveGoal}
      onClose={() => { setGoalFormOpen(false); setEditingGoalId(null); }}
      onRemove={() => { removeGoal(editingGoalId); setGoalFormOpen(false); setEditingGoalId(null); }}
      headerBg="#1B2A4A"
      saveBtnClass={`w-full ${kidTheme.primaryBtn} text-white font-bold text-sm rounded-lg py-2 mt-1`}
    />
  ) : null;
  return { startAddGoal, startEditGoal, removeGoal, toggleStar, modal };
}

// LB-385: "Save up for it" (a goal) and "Buy it now" (the LB-17 ask; a grown-up still decides
// and can make it a goal). Two EQUAL tinted buttons, because buying now is not less virtuous
// than saving up (Alan, 30 Sep). With no goal they sit in one card under "Want something?";
// under the goal cards they follow "Want something else?". Buy it now is left out where the
// app layer gives no send path (older preview fixtures, as AskToBuyCard always was), and is
// disabled while an ask is waiting: one open ask per child (LB-17), and the waiting card below
// says what it is waiting on.
export function WishListPair({ hasGoals, onSaveUp, onBuyNow, buyWaiting = false }) {
  const btn = "flex-1 min-h-[44px] bg-amber-50 text-slate-900 text-sm font-bold rounded-lg px-3 disabled:opacity-50";
  const buttons = (
    <div className="flex gap-2 mt-1">
      <button type="button" className={btn} onClick={onSaveUp} data-testid="wish-save-up">{ct("kid.wishlist.save_up", "Save up for it")}</button>
      {onBuyNow && <button type="button" className={btn} onClick={onBuyNow} disabled={buyWaiting} data-testid="wish-buy-now">{ct("kid.wishlist.buy_now", "Buy it now")}</button>}
    </div>
  );
  if (!hasGoals) {
    return (
      <div className="bg-white border border-slate-100 rounded-xl p-3" data-testid="wish-list-card">
        <div className="font-extrabold text-base text-slate-900 mb-2">{ct("kid.wishlist.want", "Want something?")}</div>
        {buttons}
      </div>
    );
  }
  return (
    <div data-testid="wish-list-more">
      <div className="mt-3 text-sm font-semibold text-slate-500 px-0.5">{ct("kid.wishlist.want_else", "Want something else?")}</div>
      {buttons}
    </div>
  );
}

export function RewardsTab({ code, child, requests, transactions, experienceRewards, onClaimReward, onRedeemGoal, onUpdateGoals, onRequestTransfer, onCancelTransfer, rewardSettings, kidTheme, vaultFocus, onFocusVault, onAskToBuy = null, adults = [] }) {
  const [askOpen, setAskOpen] = useState(false); // LB-17
  useBackLayer(askOpen, () => setAskOpen(false)); // LB-315
  const goals = child.goals || [];
  const rate = rewardSettings?.exchangeRate > 0 ? rewardSettings.exchangeRate : 10;
  const experienceRewardsAvailable = experienceRewards.filter((r) => !r.childId || r.childId === child.id).length;
  const { startAddGoal, startEditGoal, removeGoal, toggleStar, modal: goalModal } = useGoalEditor({ code, goals, rate, onUpdateGoals, kidTheme });
  const waitingAsk = onAskToBuy ? openAsk(requests, child.id) : null;

  return (
    <div className="kid-sheet flex flex-col">
      {/* Pass Q (Alan): two real columns — goals and experience rewards left; the vault
          (with a heading) and history right — instead of a 2×2 grid whose cells were
          mostly empty states. Wrappers are `contents` on a phone, so the order there is
          goals, vault, experience, history as before. */}
      <div className="contents">
        <div className="order-1">
          {/* LB-385 (Alan, 30 Sep): "My wish list". The goal cards as before, then the two equal
              ways to get something (WishListPair). The Add a goal row and Ask-to-buy's entry row
              are gone: Save up for it and Buy it now are those two entries. */}
          <CollapsibleSection
            title={ct("kid.rewards.section_goals", "My wish list")}
            tone="amber"
            icon="ic-goals"
            variant="section"
            first
            defaultOpen={true}
          >
            {goals.length > 0 && (
              <div className="flex flex-col gap-3">
                {goals.map((g) => (
                  <GoalCard key={g.id} goal={g} child={child} requests={requests} rewardSettings={rewardSettings} kidTheme={kidTheme} editable={true} onEdit={() => startEditGoal(g)} onRemove={() => removeGoal(g.id)} onRedeemGoal={onRedeemGoal} onToggleStar={toggleStar} code={code} />
                ))}
              </div>
            )}
            <WishListPair hasGoals={goals.length > 0} onSaveUp={startAddGoal} onBuyNow={onAskToBuy ? () => setAskOpen(true) : null} buyWaiting={!!waitingAsk} />
          </CollapsibleSection>

          {goalModal}

          {/* LB-17 Ask-to-buy: its WAITING face stays here, under the wish list (LB-385 replaced
              only its entry face, with Buy it now). What a grown-up decided is still told on
              Home by DecisionCard. */}
          {onAskToBuy && waitingAsk && (
            <AskToBuyCard child={child} requests={requests} rate={rate} adultName={adults.length === 1 ? (adults[0].name || "") : ""} onOpen={() => setAskOpen(true)} />
          )}
          {askOpen && (
            <AskToBuySheet
              child={child}
              code={code}
              rate={rate}
              onSend={onAskToBuy}
              onMakeGoal={(goal) => onUpdateGoals([...goals, { id: uid(), ...goal }])}
              onClose={() => setAskOpen(false)}
            />
          )}

        </div>
        <div className="order-3">
          <CollapsibleSection
            title={ct("kid.rewards.section_experiences", "Experience rewards")}
            tone="pig"
            icon="ic-gift"
            variant="section"
            defaultOpen={true}
            summary={
              <div>
                <p className="text-base text-slate-500">{ct("kid.rewards_intro", "Trade tokens for treats and privileges.")}</p>
                {experienceRewardsAvailable > 0 && <p className="text-xs text-slate-400 mt-0.5">{experienceRewardsAvailable} reward{experienceRewardsAvailable === 1 ? "" : "s"} available</p>}
              </div>
            }
          >
            {(() => {
              const globalRewards = experienceRewards.filter((r) => !r.childId);
              const childRewards = experienceRewards.filter((r) => r.childId === child.id);
              const available = [...globalRewards, ...childRewards];
              if (available.length === 0) {
                return <div className="kid-rows"><p className="kid-row text-base text-slate-500">{ct("kid.rewards_empty", "Ask your parent to set up experience rewards, like extra screen time or choosing dinner.")}</p></div>;
              }
              // Only one reward claim can be waiting on a parent at a time — mirrors the vault
              // transfer's one-pending-at-a-time rule, so a kid can't rack up several claims
              // before a parent has even seen the first one.
              const pendingClaim = requests.find((req) => req.requestType === "experience_redeem" && req.status === "pending");
              return (
                <div className="flex flex-col gap-2">
                  {available.map((r) => {
                    const canAfford = child.tokenBalance >= r.tokens;
                    const isThisPending = pendingClaim?.experienceRewardId === r.id;
                    const blockedByOther = !!pendingClaim && !isThisPending;
                    return (
                      <div key={r.id} className={`flex items-center justify-between border border-slate-100 rounded-xl overflow-hidden ${!isThisPending && canAfford && !blockedByOther ? "" : ""}`}>
                        {isThisPending ? (
                          <div className="flex items-center justify-between w-full px-3 py-2.5">
                            <span className="text-sm font-semibold text-slate-700 flex-1 min-w-0 truncate">{r.label}</span>
                            <span className="text-xs font-semibold text-amber-600 bg-amber-50 rounded px-2.5 py-1 flex-shrink-0"><Icon id="ic-pending" size={12} className="inline-block align-[-2px] mr-1" style={{ color: "currentColor" }} />{ct("kid.pending_chip", "pending")}</span>
                          </div>
                        ) : (
                          <button disabled={!canAfford || blockedByOther} title={blockedByOther ? "You already have a reward claim waiting on a parent" : ""} onClick={() => onClaimReward(r)}
                            className={`w-full flex items-center justify-between gap-3 px-3 py-3 text-left ${canAfford && !blockedByOther ? "active:bg-emerald-50" : ""}`}>
                            <span className="text-sm font-semibold text-slate-700 flex-1 min-w-0 truncate">{r.label}</span>
                            <span className={`text-xs font-medium rounded-lg px-4 py-2 flex-shrink-0 ${canAfford && !blockedByOther ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>
                              {r.tokens} <CoinIcon className="w-3 h-3 inline-block" />
                            </span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {pendingClaim && (
                    <p className="text-base text-amber-600 font-semibold text-center mt-1">You already have a reward claim waiting on a parent — you can claim another once that's resolved.</p>
                  )}
                </div>
              );
            })()}
          </CollapsibleSection>
        </div>
      </div>
      <div className="contents">
        <div className="order-2">
          {/* LB-385: one My Savings Vault card (head, balance, line, transfer bar, tiles). */}
          <SavingsVaultCard child={child} requests={requests} onRequestTransfer={onRequestTransfer} onCancelTransfer={onCancelTransfer} rewardSettings={rewardSettings} kidTheme={kidTheme} focusKey={vaultFocus?.key} focusAmount={vaultFocus?.amount} />
        </div>
        <div className="order-4">
          <CollapsibleSection title="Transaction History" tone="amber" icon="ic-recent" variant="section">
            <div className="kid-rows"><ActivityList
              items={transactions}
              emptyText="No activity yet."
              renderRow={(t) => {
                const style = activityRowStyle(t);
                return (
                  <ActivityRow
                    key={t.id}
                    {...style}
                    label={t.reason}
                    timestamp={fmtActivityStamp(t.timestamp)}
                    status={t.status}
                    reversed={t.reversed}
                    isReversal={!!t.reversalOf}
                  />
                );
              }}
            /></div>
          </CollapsibleSection>
        </div>
      </div>
    </div>
  );
}

export { useGoalEditor };
